import { Fragment, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Clock,
  MapPin,
  Ban,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useI18n } from "@/hooks/useI18n";
import { PaymentStatusCell } from "@/lib/dashboard/shared/PaymentStatusCell";
import {
  formatPlanAmount,
  formatPlanDate,
} from "@/lib/builder/utils/payment-format";
import { PlatformFeeDueBadge } from "@/lib/payments/components/PlatformFeeDueBadge";
import {
  DetailInstallment,
  DetailItinerary,
} from "@/integrations/supabase/rpc-types";
import type { TranslationKey } from "@/types/i18n.types";

/**
 * Etichetta di stato itinerario.
 *
 * Gli stati arrivano dal database (`itineraries.status`) e le chiavi di `itineraryStatus`
 * li ricalcano uno a uno, ma un valore nuovo lato DB non deve stampare "itineraryStatus.x"
 * in faccia all'utente: senza traduzione si mostra lo stato grezzo.
 */
function itineraryStatusLabel(status: string, t: (key: TranslationKey) => string): string {
  const label = t(`itineraryStatus.${status}` as TranslationKey);
  return label === `itineraryStatus.${status}` ? status : label;
}

function itineraryStatusTone(status: string): string {
  switch (status) {
    case "completed":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "rejected":
    case "expired":
      return "bg-gray-100 text-gray-600 border-gray-200";
    case "draft":
      return "bg-gray-50 text-gray-500 border-gray-200";
    default:
      return "bg-amber-50 text-amber-700 border-amber-200";
  }
}

interface InstallmentRowProps {
  installment: DetailInstallment;
  /** Rate non annullate del piano, per la dicitura "rata 2 di 4". */
  total: number;
  columnSpan: number;
}

function InstallmentRow({ installment, total, columnSpan }: InstallmentRowProps) {
  const { t } = useI18n();
  const cancelled = installment.status === "cancelled";
  const paid = installment.status === "paid";

  const icon = cancelled ? (
    <Ban className="h-3.5 w-3.5 text-gray-400" />
  ) : paid ? (
    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
  ) : installment.is_overdue ? (
    <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
  ) : (
    <Clock className="h-3.5 w-3.5 text-amber-500" />
  );

  const statusText = cancelled
    ? t("dashboard.overview.detail.installmentCancelled")
    : paid
      ? t("dashboard.overview.detail.installmentPaidOn", {
          date: formatPlanDate(installment.paid_at),
        })
      : installment.is_overdue
        ? t("dashboard.overview.detail.installmentOverdue")
        : t("dashboard.overview.detail.installmentPending");

  return (
    /*
      Due righe per lato, allineate: a sinistra "Rata 2 di 4" con sotto la scadenza in
      piccolo, a destra l'importo con sotto lo stato. Piu' stretta della riga unica, che
      con etichetta, data e badge andava a capo comunque.
    */
    <TableRow className="bg-gray-50/60 hover:bg-gray-50">
      <TableCell />
      <TableCell colSpan={columnSpan - 2} className="py-2">
        <div className="flex items-start gap-2">
          <span className="mt-0.5 flex-shrink-0">{icon}</span>
          <div className="flex flex-col leading-tight min-w-0">
            <div
              className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-sm ${
                cancelled ? "text-gray-400 line-through" : "text-gray-700"
              }`}
            >
              <span className="font-medium">
                {t("dashboard.overview.detail.installmentSeq", {
                  seq: installment.seq,
                  total,
                })}
              </span>
              {installment.label && (
                <span className="text-gray-500 truncate">· {installment.label}</span>
              )}
              {/* La domanda a cui questo dettaglio deve rispondere non e' solo "e' stata
                  pagata" ma "su questa rata devo ancora qualcosa": senza il badge, un
                  incasso a mano e uno passato da Stripe si leggono identici. */}
              <PlatformFeeDueBadge amount={installment.platform_fee_due} />
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {formatPlanDate(installment.due_date)}
            </span>
          </div>
        </div>
      </TableCell>
      <TableCell className="py-2 text-right whitespace-nowrap">
        <div className="flex flex-col items-end leading-tight">
          <span
            className={`text-sm ${cancelled ? "text-gray-400 line-through" : ""}`}
          >
            {formatPlanAmount(installment.amount)}
          </span>
          <span
            className={`text-xs ${
              installment.is_overdue ? "text-red-600 font-medium" : "text-gray-500"
            }`}
          >
            {statusText}
          </span>
        </div>
      </TableCell>
    </TableRow>
  );
}

interface DetailItinerariesTableProps {
  itineraries: DetailItinerary[];
  /**
   * Chi qualifica la riga sotto il titolo del viaggio: nella scheda di un cliente e' il
   * consulente che l'ha seguito, in quella di un consulente e' il cliente. Ripetere il
   * nome della persona di cui si sta gia' leggendo la scheda sarebbe rumore.
   */
  subtitle?: "advisor" | "client";
  /** Tutte le righe aperte all'ingresso: la scheda viaggio ha una riga sola, e le rate
   *  sono proprio cio' che si e' venuti a vedere. */
  defaultExpanded?: boolean;
}

/**
 * Tabella unica di itinerari e rate: una riga per viaggio con i suoi totali, e le rate
 * annidate sotto quando la riga e' aperta.
 *
 * Le rate non stanno in una tabella separata perche' la domanda che ci si fa aprendo la
 * scheda e' "questo cliente e' in regola?", e la risposta e' la somma di viaggi e
 * scadenze: separarle costringerebbe a incrociarle a mano.
 */
export function DetailItinerariesTable({
  itineraries,
  subtitle = "advisor",
  defaultExpanded = false,
}: DetailItinerariesTableProps) {
  const { t } = useI18n();
  // Aperto di default cio' che ha una rata scaduta: e' l'unica riga che chiede un'azione,
  // e trovarla gia' espansa risparmia il click che serviva a scoprirla.
  const [expanded, setExpanded] = useState<Set<string>>(
    () =>
      new Set(
        itineraries
          .filter((it) => defaultExpanded || it.overdue_amount > 0)
          .map((it) => it.itinerary_id)
      )
  );

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  // Colonne: espansore, viaggio, stato, stato pagamenti. Le date del viaggio stavano qui
  // ma non rispondevano a nessuna domanda della scheda, che parla di denaro: restano
  // nell'itinerario, dove si va per sapere quando si parte. Totale, pagato e residuo
  // erano tre colonne separate e ora stanno in una cella sola.
  const COLUMN_COUNT = 4;

  if (itineraries.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center gap-2 py-10 text-center text-gray-500">
            <MapPin className="h-8 w-8 text-gray-300" />
            <p className="text-sm">{t("dashboard.overview.detail.noItineraries")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>{t("dashboard.overview.detail.columnItinerary")}</TableHead>
                <TableHead>{t("dashboard.overview.detail.columnStatus")}</TableHead>
                <TableHead className="text-right">
                  {t("dashboard.overview.paymentStatus.column")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {itineraries.map((itinerary) => {
                const isOpen = expanded.has(itinerary.itinerary_id);
                const canExpand = itinerary.installments.length > 0;

                return (
                  <Fragment key={itinerary.itinerary_id}>
                    <TableRow
                      className={canExpand ? "cursor-pointer" : ""}
                      onClick={() => canExpand && toggle(itinerary.itinerary_id)}
                    >
                      <TableCell className="pr-0">
                        {canExpand ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggle(itinerary.itinerary_id);
                            }}
                            aria-expanded={isOpen}
                            aria-label={t(
                              isOpen
                                ? "dashboard.overview.detail.collapseItinerary"
                                : "dashboard.overview.detail.expandItinerary"
                            )}
                            className="p-1 text-gray-400 hover:text-gray-700"
                          >
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        ) : null}
                      </TableCell>

                      <TableCell className="font-medium text-gray-900">
                        <div className="flex flex-col gap-0.5">
                          <span>{itinerary.title}</span>
                          <span className="text-xs text-gray-500">
                            {itinerary.destinations.join(", ") || "—"}
                            {(() => {
                              const who =
                                subtitle === "client"
                                  ? itinerary.client_name
                                  : itinerary.advisor_name;
                              return who ? ` · ${who}` : null;
                            })()}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge
                          variant="outline"
                          className={itineraryStatusTone(itinerary.status)}
                        >
                          {itineraryStatusLabel(itinerary.status, t)}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-right">
                        {/* Il totale e' quello del PIANO, non `itinerary.total`: deve
                            chiudere con i due addendi sotto la barra. Senza piano la
                            terna non esiste e la cella mostra un trattino -- uno zero
                            si leggerebbe come un mancato pagamento. */}
                        <PaymentStatusCell
                          compact
                          paidAmount={itinerary.paid_amount}
                          pendingAmount={itinerary.remaining_amount}
                          overdueAmount={itinerary.overdue_amount}
                          totalAmount={itinerary.plan_total}
                          empty={!itinerary.has_plan}
                        />
                      </TableCell>
                    </TableRow>

                    {isOpen &&
                      itinerary.installments.map((installment) => (
                        <InstallmentRow
                          key={installment.installment_id}
                          installment={installment}
                          total={itinerary.installments_count}
                          columnSpan={COLUMN_COUNT}
                        />
                      ))}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export default DetailItinerariesTable;
